import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import type { GetToken } from "../types/auth.types";

/**
 * Clerk's `getToken`, behind a reference that never changes.
 *
 * `useAuth()` hands back a fresh function on some renders, so an effect that
 * depends on Clerk's own re-runs on each of them. For a fetch that is a
 * request per render; for an effect that also sets state it is a loop, and
 * for one that sets a timer the timer is cleared and restarted before it can
 * ever elapse.
 *
 * Depending on the value this returns is safe: it is created once and reads
 * the current `getToken` through a ref when called. `useClientLookups` does
 * the same thing inline - this is that pattern, for the hooks that need to
 * pass a token getter down to a service rather than call it in place.
 */
export function useTokenGetter(): GetToken {
  const { getToken } = useAuth();
  const ref = useRef(getToken);

  useEffect(() => {
    ref.current = getToken;
  }, [getToken]);

  return useCallback(() => ref.current(), []);
}

export default useTokenGetter;
